/** @jsxImportSource @emotion/react */

import css from '@emotion/css/macro'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { formatCurrency } from '@sportninja/common/utils/utils'
import {
  getBillingTypeLabel,
  getPayableRegistrationTypeLabel,
} from '@sportninja/common/utils/billing'
import { Collapse } from 'antd'
import { useLayoutEffect, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Icon from '../../components/Icon'
import { useSelector, useDispatch } from 'src/hooks/useRedux'
import {
  registrationActions,
  cleanUpPaymentData,
} from '@sportninja/common/actions/registration'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'

export const HomePaymentBanner = ({ isInFrame }) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const isHome = useMemo(() => location.pathname === '/home', [location])
  const hasDepositPaymentAvailable = useSelector(
    (state) => state.registration.hasDepositPaymentAvailable
  )
  const hasInstallmentsPaymentAvailable = useSelector(
    (state) => state.registration.hasInstallmentsPaymentAvailable
  )
  const history = useHistory()

  useLayoutEffect(() => {
    if (isHome) {
      dispatch(registrationActions.checkPayments.request(null, null))
    }
  }, [isHome, dispatch])

  if (isInFrame) {
    return null
  }

  if (
    !isHome ||
    (hasDepositPaymentAvailable.length === 0 &&
      hasInstallmentsPaymentAvailable.length === 0)
  ) {
    return null
  }

  const items = []
  // Full rows (admin pay-in-full consolidations) and SN-8310 added invoices ride the
  // installments feed — label them honestly instead of "Installment". Only the row's
  // own type: this names ONE payable row, never the registration's plan.
  const paymentTypeLabel = (item) =>
    getBillingTypeLabel({ payment_type: item?.payment_type })
  const label = (isDeposit, item) => {
    return (
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
        `}
      >
        <Icon
          name='exclamation-triangle'
          color={colors.CAUTION_DARK}
          fontSize={16}
        />
        <p
          css={css`
            color: ${colors.CAUTION_DARK};
            font-size: 16px;
            font-weight: 700;
            line-height: 145%;
          `}
        >
          {isDeposit
            ? 'Pay Your Deposit Now'
            : `Pay Your ${paymentTypeLabel(item)} Now`}
        </p>
      </div>
    )
  }

  const children = (item, isDeposit) => {
    const dueDate = new Date(item?.due_date)
    const formattedDueDate = `${dueDate.toLocaleString('default', {
      month: 'long',
    })} ${dueDate.getDate()}, ${dueDate.getFullYear()}`
    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 4px;
          align-self: stretch;
          background-color: ${colors.FLORAL_WHITE};
        `}
      >
        {item?.registration_type === 'Player' && item?.player_name ? (
          <a
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
            `}
            onClick={() =>
              history.push(`/team/${item?.team_id}/registration?tab=billing`)
            }
          >
            <div
              css={css`
                display: flex;
                width: 24px;
                height: 24px;
                justify-content: center;
                align-items: center;
                border-radius: 8px;
                background-color: ${colors.WHITE};
              `}
            >
              <img
                src={
                  getImageThumbnailId({ image: item?.player_image }) ||
                  '/images/generic_placeholders/placeholder_player.png'
                }
                css={css`
                  width: 100%;
                  height: 100%;
                  border-radius: 4px;
                `}
                onError={(e) => {
                  e.target.src =
                    '/images/generic_placeholders/placeholder_player.png'
                }}
              />
            </div>
            <p
              css={css`
                color: ${colors.HEADER};
                font-size: 20px;
                font-weight: 600;
                font-family: ${isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                  : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
              `}
            >
              {item?.player_name}
            </p>
          </a>
        ) : (
          <a
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
            `}
            onClick={() =>
              history.push(`/team/${item?.team_id}/registration?tab=billing`)
            }
          >
            {resolveAssetUrl(item?.team_image) ? (
              <div
                css={css`
                  display: flex;
                  width: 24px;
                  height: 24px;
                  justify-content: center;
                  align-items: center;
                  border-radius: 4px;
                  background-color: ${colors.WHITE};
                `}
              >
                <img
                  src={getImageThumbnailId(item)}
                  css={css`
                    width: 100%;
                    height: 100%;
                    border-radius: 4px;
                  `}
                  onError={(e) => {
                    e.target.src =
                      '/images/generic_placeholders/placeholder_team.png'
                  }}
                />
              </div>
            ) : (
              <div
                css={css`
                  display: flex;
                  width: 24px;
                  height: 24px;
                  justify-content: center;
                  align-items: center;
                  border-radius: 4px;
                  background-color: ${colors.WHITE};
                `}
              >
                <img
                  src={'/images/generic_placeholders/placeholder_team.png'}
                  css={css`
                    width: 100%;
                    height: 100%;
                    border-radius: 4px;
                  `}
                  onError={(e) => {
                    e.target.src =
                      '/images/generic_placeholders/placeholder_team.png'
                  }}
                />
              </div>
            )}
            <p
              css={css`
                color: ${colors.HEADER};
                font-size: 20px;
                font-weight: 600;
                font-family: ${isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                  : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
              `}
            >
              {item?.registration_type === 'Team'
                ? item?.team_name
                : item?.player_name}
            </p>
          </a>
        )}
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
          `}
        >
          <p
            css={css`
              color: ${colors.HEADER};
              font-size: 16px;
              font-weight: 700;
              line-height: 145%;
            `}
          >
            Type:
          </p>
          <p
            css={css`
              color: ${colors.HEADER};
              font-size: 16px;
              line-height: 145%;
            `}
          >
            {getPayableRegistrationTypeLabel(item ?? {})}
          </p>
        </div>
        {item?.registration_type === 'Player' && (
          <a
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
            `}
            onClick={() =>
              history.push(`/team/${item?.team_id}/registration?tab=billing`)
            }
          >
            <p
              css={css`
                color: ${colors.HEADER};
                font-size: 16px;
                font-weight: 700;
                line-height: 145%;
              `}
            >
              Team:
            </p>
            {resolveAssetUrl(item?.team_image) ? (
              <div
                css={css`
                  display: flex;
                  width: 20px;
                  height: 20px;
                  justify-content: center;
                  align-items: center;
                  border-radius: 4px;
                  background-color: ${colors.WHITE};
                `}
              >
                <img
                  src={getImageThumbnailId(item)}
                  css={css`
                    width: 100%;
                    height: 100%;
                    border-radius: 4px;
                  `}
                  onError={(e) => {
                    e.target.src =
                      '/images/generic_placeholders/placeholder_team.png'
                  }}
                />
              </div>
            ) : (
              <div
                css={css`
                  display: flex;
                  width: 20px;
                  height: 20px;
                  justify-content: center;
                  align-items: center;
                  border-radius: 4px;
                  background-color: ${colors.WHITE};
                `}
              >
                <img
                  src={'/images/generic_placeholders/placeholder_team.png'}
                  css={css`
                    width: 100%;
                    height: 100%;
                    border-radius: 4px;
                  `}
                  onError={(e) => {
                    e.target.src =
                      '/images/generic_placeholders/placeholder_team.png'
                  }}
                />
              </div>
            )}
            <p
              css={css`
                color: ${colors.HEADER};
                font-size: 16px;
                line-height: 145%;
              `}
            >
              {item?.team_name}
            </p>
          </a>
        )}
        <a
          css={css`
            display: flex;
            align-items: center;
          `}
          onClick={() => history.push(`/schedule/${item?.schedule_id}`)}
        >
          <p
            css={css`
              color: ${colors.HEADER};
              font-size: 16px;
              font-weight: 700;
              line-height: 145%;
            `}
          >
            {isCanlan ? 'Season' : 'Competition'}:
          </p>
          <p
            css={css`
              color: ${colors.HEADER};
              font-size: 16px;
              margin-left: 8px;
              line-height: 145%;
            `}
          >
            {item?.root_schedule_name} - {item?.schedule_name}
          </p>
        </a>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
          `}
        >
          <p
            css={css`
              color: ${colors.HEADER};
              font-size: 16px;
              font-weight: 700;
              line-height: 145%;
            `}
          >
            {isDeposit ? 'Deposit:' : `${paymentTypeLabel(item)}:`}
          </p>
          <p
            css={css`
              color: ${colors.HEADER};
              font-size: 16px;
              line-height: 145%;
            `}
          >
            {formatCurrency(
              isDeposit ? item?.deposit_amount : item?.payment_amount,
              item?.currency_country?.currency_code || 'CAD'
            )}
          </p>
        </div>
        {item?.due_date ? (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
            `}
          >
            <p
              css={css`
                color: ${colors.HEADER};
                font-size: 16px;
                font-weight: 700;
                line-height: 145%;
              `}
            >
              Due Date :
            </p>
            <p
              css={css`
                color: ${colors.HEADER};
                font-size: 16px;
                line-height: 145%;
              `}
            >
              {formattedDueDate}
            </p>
          </div>
        ) : null}
        <button
          css={css`
            display: flex;
            padding: 8px;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            align-self: stretch;
            background-color: ${colors.PRIMARY};
            border-radius: 8px;
            color: ${PRIMARY_BUTTON_CONTENT.text};
            font-size: 20px;
            font-weight: 600;
            font-family: ${isCanlan
              ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
              : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
          `}
          onClick={() => {
            // For Player, we need to use the rosterId and the billingId
            // For Division, we need to use the divisionId and the teamId
            if (item?.registration_type === 'Player') {
              history.push(
                `${ROUTES.REGISTRATION_PAYMENT}?teamId=${item?.team_id}&rosterId=${item?.team_roster_id}&billingId=${item?.id}`
              )
            } else {
              history.push(
                `${ROUTES.REGISTRATION_PAYMENT}?divisionId=${item?.root_schedule_id}&teamId=${item?.team_id}&billingId=${item?.id}`
              )
            }
            setTimeout(() => {
              dispatch(cleanUpPaymentData())
            }, 200)
          }}
        >
          PAY NOW
        </button>
      </div>
    )
  }

  if (hasDepositPaymentAvailable?.length > 0) {
    hasDepositPaymentAvailable.forEach((item, index) => {
      items.push({
        key: `${index}_${item?.id}`,
        label: label(true, item),
        children: children(item, true),
        style: {
          marginBottom: 8,
          borderRadius: 8,
          backgroundColor: colors.FLORAL_WHITE,
        },
      })
    })
  }

  if (hasInstallmentsPaymentAvailable?.length > 0) {
    hasInstallmentsPaymentAvailable.forEach((item, index) => {
      items.push({
        key: `${index}_${item?.id}`,
        label: label(false, item),
        children: children(item, false),
        style: {
          marginBottom: 8,
          borderRadius: 8,
          backgroundColor: colors.FLORAL_WHITE,
        },
      })
    })
  }

  // I need to create an array that contains the key of the first item of the deposits and the first item of the payments,
  // so I can use it as the defaultActiveKey
  const defaultActiveKey = []
  if (hasDepositPaymentAvailable?.length > 0) {
    defaultActiveKey.push(`0_${hasDepositPaymentAvailable[0]?.id}`)
  }
  if (hasInstallmentsPaymentAvailable?.length > 0) {
    defaultActiveKey.push(`0_${hasInstallmentsPaymentAvailable[0]?.id}`)
  }

  return (
    <Collapse
      defaultActiveKey={defaultActiveKey}
      accordion
      bordered={true}
      items={items}
      css={css`
        border: none;
        background-color: transparent;
        .ant-collapse-header {
          color: ${colors.CAUTION_DARK}!important;
          font-weight: 700;
          line-height: 145%;
          font-size: 16px;
        }
        .ant-collapse-item {
          border: 2px solid ${'#FFC107'};
          border-radius: 0 0 8px 8px;
        }
        .ant-collapse-content-box {
          background-color: ${colors.FLORAL_WHITE}!important;
          /* add the border radius of 8px only on the bottom right and the bottom left */
          border-radius: 0 0 8px 8px !important;
          /* padding 16 on all sides besides the top */
          padding: 0 16px 16px 16px !important;
        }
        .ant-collapse-content {
          border-top: none !important;
          border-radius: 0 0 8px 8px !important;
        }
      `}
    />
  )
}
